/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircledIcon,
  Cross2Icon,
  FileTextIcon,
  IdCardIcon,
} from '@/shared/ui/radix-icons';
import {
  ChoiceChip,
  FilterPanelShell,
  FilterSection,
  Text,
  UnstyledButton,
} from '@/shared/ui';
import {
  SALE_STATES,
  SALE_STATE_STYLES,
  useSaleStateLabel,
  type SaleState,
} from '../../constants/sale-states';
import {
  PAPERWORK_STATUSES,
  VALUE_BRACKETS,
  countSaleFilters,
  type PaperworkStatusId,
  type SaleFilters,
  type ValueBracketId,
} from './sale-filters';

export interface SaleFilterPanelProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: SaleFilters) => void;
  onClear: () => void;
  initialFilters?: SaleFilters;
  /** Brokers present in the records; a single one hides the section. */
  brokers?: string[];
}

/**
 * Toggles *value* in *list*, returning a new array.
 *
 * @param list - The current selection, possibly unset.
 * @param value - The entry to add or remove.
 * @returns The new selection.
 */
function toggle<T>(list: T[] | undefined, value: T): T[] {
  const current = list ?? [];
  return current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];
}

/**
 * The Vendas filter panel.
 *
 * Same shell, sections and chip behaviour as the lease and visit panels, so
 * the drawer opens where the broker expects and the chips behave the way they
 * already learned.
 *
 * Choices are staged locally and only lifted on Apply — the list does not
 * churn under the broker while they are still picking.
 */
export function SaleFilterPanel({
  open,
  onClose,
  onApply,
  onClear,
  initialFilters = {},
  brokers = [],
}: SaleFilterPanelProps) {
  const { t } = useTranslation('features/sales');
  const stateLabel = useSaleStateLabel();
  const [filters, setFilters] = useState<SaleFilters>(initialFilters);

  // Re-seeds on open so a cancelled session does not leak into the next.
  useEffect(() => {
    if (open) setFilters(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const activeCount = countSaleFilters(filters);

  if (!open) return null;

  return (
    <FilterPanelShell
      testIdPrefix="sale-"
      layout="sibling"
      title={t('filters.title')}
      onClose={onClose}
      onClear={() => { setFilters({}); onClear(); }}
      onApply={() => { onApply(filters); onClose(); }}
      activeCount={activeCount}
      clearLabel={t('filters.clear')}
      applyLabel={t('filters.apply', { count: activeCount })}
      closeIcon={<Cross2Icon width={14} height={14} />}
      ButtonComponent={UnstyledButton}
      TextComponent={({ children, style }) => (
        <Text
          as="span"
          style={style ?? {
            fontSize: 'var(--text-body-sm)',
            fontWeight: 'var(--font-bold)' as unknown as number,
            color: 'var(--seed-base)',
          }}
        >
          {children}
        </Text>
      )}
      backdropStyle={{
        position: 'fixed', top: 'var(--header-height)', left: 0, right: 0, bottom: 0,
        background: 'var(--overlay-scrim)', backdropFilter: 'blur(2px)', zIndex: 100,
      }}
      panelStyle={{
        position: 'fixed', top: 'var(--header-height)', right: 0, bottom: 0,
        width: '340px', maxWidth: '90vw', background: 'var(--surface-primary)',
        boxShadow: 'var(--shadow-lg)', zIndex: 101,
        display: 'flex', flexDirection: 'column',
        fontFamily: 'var(--font-family)', overflow: 'hidden',
      }}
      headerStyle={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'var(--space-3) var(--space-4)',
        background: 'color-mix(in srgb, var(--seed-base) 5%, var(--surface-primary))',
        borderBottom: '1px solid color-mix(in srgb, var(--seed-base) 10%, transparent)',
      }}
      closeButtonStyle={{
        width: 28, height: 28, borderRadius: 'var(--radius-md)', border: 'none',
        background: 'transparent', color: 'var(--text-tertiary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
      }}
      bodyStyle={{
        flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 'var(--space-4)',
        display: 'flex', flexDirection: 'column', gap: 'var(--space-5)',
      }}
      footerStyle={{
        display: 'flex', gap: 'var(--space-2)',
        padding: 'var(--space-3) var(--space-4)', borderTop: '1px solid var(--border-subtle)',
      }}
      clearButtonStyle={{
        flex: 1, padding: 'var(--space-2)', fontSize: 'var(--text-meta)',
        fontWeight: 'var(--font-semibold)' as unknown as number, background: 'transparent',
        color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)', cursor: 'pointer', fontFamily: 'var(--font-family)',
      }}
      applyButtonStyle={{
        flex: 1, padding: 'var(--space-2)', fontSize: 'var(--text-meta)',
        fontWeight: 'var(--font-bold)' as unknown as number,
        background: 'var(--button-primary-bg)', color: 'var(--button-primary-text)',
        border: 'none', borderRadius: 'var(--radius-md)', cursor: 'pointer',
        fontFamily: 'var(--font-family)',
      }}
    >
      <FilterSection icon={<CheckCircledIcon width={14} height={14} />} title={t('filters.state')}>
        {SALE_STATES.map((state) => (
          <ChoiceChip
            key={state}
            label={stateLabel(state)}
            active={(filters.states ?? []).includes(state)}
            accent={SALE_STATE_STYLES[state]}
            onToggle={() => setFilters((f) => ({
              ...f,
              states: toggle<SaleState>(f.states, state),
            }))}
          />
        ))}
      </FilterSection>

      <FilterSection icon={<FileTextIcon width={14} height={14} />} title={t('filters.paperwork')}>
        {PAPERWORK_STATUSES.map((status) => (
          <ChoiceChip
            key={status}
            label={t(`filters.paperworkStatuses.${status}`)}
            active={(filters.paperwork ?? []).includes(status)}
            onToggle={() => setFilters((f) => ({
              ...f,
              paperwork: toggle<PaperworkStatusId>(f.paperwork, status),
            }))}
          />
        ))}
      </FilterSection>

      <FilterSection icon={<Text as="span">R$</Text>} title={t('filters.value')}>
        {VALUE_BRACKETS.map((bracket) => (
          <ChoiceChip
            key={bracket.id}
            label={t(`filters.valueBrackets.${bracket.id}`)}
            active={(filters.valueBrackets ?? []).includes(bracket.id)}
            onToggle={() => setFilters((f) => ({
              ...f,
              valueBrackets: toggle<ValueBracketId>(f.valueBrackets, bracket.id),
            }))}
          />
        ))}
      </FilterSection>

      {/* Hidden when a single broker answers for everything: the section
          would be a one-option no-op, as in the lease and visit panels. */}
      {brokers.length > 1 && (
        <FilterSection icon={<IdCardIcon width={14} height={14} />} title={t('filters.broker')}>
          {brokers.map((email) => (
            <ChoiceChip
              key={email}
              label={email}
              active={(filters.brokers ?? []).includes(email)}
              onToggle={() => setFilters((f) => ({
                ...f,
                brokers: toggle<string>(f.brokers, email),
              }))}
            />
          ))}
        </FilterSection>
      )}
    </FilterPanelShell>
  );
}

export default SaleFilterPanel;
export * from './sale-filters';
